import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Suspense, useContext, useEffect, useMemo, useState, useRef, forwardRef, Fragment } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { Box, xcss } from '@atlaskit/primitives';
import { useMergeRefs } from 'use-callback-ref';
import { EnvironmentContext } from '../../context';
import { useInvokeExtension } from '../../web-client';
import { ThreeLOPrompt as _ThreeLOPrompt } from '../../components';
import { useBridge, useProductFetchClient, useNavigation, createBridgeApi, isBinaryContentType } from '../bridge';
import { useTracingContext } from '../../error-reporting';
import { getBrowserLocale, getBrowserTimezone, getResolverContext, getViewContext } from './utils';
import { isAppConsentedLocally, setAppConsentLocal } from './localAppConsent';
import React from 'react';
import { getLoadingComponent } from './utils/getLoadingComponent';
var defaultComponents = {
  ThreeLOPrompt: function ThreeLOPrompt(props) {
    return jsx(_ThreeLOPrompt, props);
  }
};

/**
 * Hook for determining default IFrame React ref, using parent ref when provided
 *
 * @param parentIframeRef parent IFrame React ref
 * @returns default IFrame React ref
 */
function useDefaultIframeRef(parentIframeRef) {
  var localIframeRef = useRef(null);
  var mergedRef = useMergeRefs([parentIframeRef, localIframeRef]);
  return mergedRef;
}
export var InnerIframe = /*#__PURE__*/forwardRef(function (_ref, parentIframeRef) {
  var accountId = _ref.accountId,
    extension = _ref.extension,
    contextIds = _ref.contextIds,
    apolloClient = _ref.apolloClient,
    components = _ref.components,
    coreData = _ref.coreData,
    extensionData = _ref.extensionData,
    loadingComponent = _ref.loadingComponent,
    onLoad = _ref.onLoad,
    _ref$height = _ref.height,
    height = _ref$height === void 0 ? '100%' : _ref$height,
    _ref$width = _ref.width,
    width = _ref$width === void 0 ? '100%' : _ref$width,
    src = _ref.src,
    bridge = _ref.bridge,
    customBridgeMethods = _ref.customBridgeMethods,
    isResizable = _ref.isResizable,
    getContextToken = _ref.getContextToken,
    timezone = _ref.timezone,
    locale = _ref.locale,
    setIsThreeLOPromptVisible = _ref.setIsThreeLOPromptVisible,
    extensionPayload = _ref.extensionPayload,
    isHidden = _ref.isHidden,
    theme = _ref.theme,
    surfaceColor = _ref.surfaceColor,
    appId = _ref.appId;
  var _ref2 = components ? components(defaultComponents) : defaultComponents,
    ThreeLOPrompt = _ref2.ThreeLOPrompt;
  var appIsAuthenticated = isAppConsentedLocally(appId, accountId);
  var consentUrl = extension.consentUrl,
    currentUserConsent = extension.currentUserConsent,
    requiresUserConsent = extension.requiresUserConsent;
  var _useState = useState(consentUrl && !currentUserConsent && requiresUserConsent && !appIsAuthenticated),
    _useState2 = _slicedToArray(_useState, 2),
    userConsentRequired = _useState2[0],
    setUserConsentRequired = _useState2[1];
  var productContext = useMemo(function () {
    var resolverContext = getResolverContext(coreData, extensionData);
    var viewContext = getViewContext(resolverContext, accountId, extension, timezone !== null && timezone !== void 0 ? timezone : getBrowserTimezone(), locale !== null && locale !== void 0 ? locale : getBrowserLocale(), extensionPayload, theme, surfaceColor);
    return {
      resolverContext: resolverContext,
      viewContext: viewContext
    };
  }, [coreData, extensionData, accountId, extension, timezone, locale, extensionPayload, theme, surfaceColor]);
  var tracing = useTracingContext();
  var contextTokenRef = useRef(undefined);
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    threeLOInfo = _useState4[0],
    setThreeLOInfo = _useState4[1];
  var invokeRetriesRef = useRef([]);
  var iframeRef = useDefaultIframeRef(parentIframeRef);
  useEffect(function () {
    var clearThreeLOInfo = /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              return _context.abrupt("return", setThreeLOInfo(null));
            case 1:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function clearThreeLOInfo() {
        return _ref3.apply(this, arguments);
      };
    }();
    if (userConsentRequired) {
      tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - start');
      setThreeLOInfo({
        authUrl: consentUrl,
        onSuccess: function () {
          var _onSuccess = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
            return _regeneratorRuntime.wrap(function _callee2$(_context2) {
              while (1) switch (_context2.prev = _context2.next) {
                case 0:
                  tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - end', 'success');
                  setUserConsentRequired(false);
                  setIsThreeLOPromptVisible(false);
                  setAppConsentLocal(appId, accountId);
                  return _context2.abrupt("return", clearThreeLOInfo());
                case 5:
                case "end":
                  return _context2.stop();
              }
            }, _callee2);
          }));
          function onSuccess() {
            return _onSuccess.apply(this, arguments);
          }
          return onSuccess;
        }(),
        onReject: function onReject() {
          tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - end', 'rejected');
          return clearThreeLOInfo();
        }
      });
      setIsThreeLOPromptVisible(true);
      if (onLoad) {
        onLoad();
      }
    }
  }, [onLoad, setIsThreeLOPromptVisible, tracing, appId, accountId, consentUrl, userConsentRequired]);
  var navigation = useNavigation({
    extension: extension,
    push: bridge === null || bridge === void 0 ? void 0 : bridge.spaPush
  });
  var invoker = useInvokeExtension(contextIds, extension.id, {
    client: apolloClient
  });
  var environment = useContext(EnvironmentContext);
  var cloudId = coreData.cloudId,
    workspaceId = coreData.workspaceId;
  var productFetchClient = useProductFetchClient({
    environment: environment,
    contextIds: contextIds,
    cloudId: cloudId,
    workspaceId: workspaceId,
    extensionId: extension.id,
    options: {
      client: apolloClient
    },
    renderThreeLOPrompt: function renderThreeLOPrompt(retry) {
      return new Promise(function (resolve, reject) {
        var authUrl = (threeLOInfo === null || threeLOInfo === void 0 ? void 0 : threeLOInfo.authUrl) || consentUrl;
        if (authUrl) {
          invokeRetriesRef.current.push(retry);
          tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - start');
          setThreeLOInfo({
            authUrl: authUrl,
            onSuccess: function onSuccess() {
              tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - end', 'success');
              resolve();
              setThreeLOInfo(null);
              setIsThreeLOPromptVisible(false);
              return Promise.all(invokeRetriesRef.current.map(function (retry) {
                return retry();
              })).then(function () {
                invokeRetriesRef.current = [];
              });
            },
            onReject: function onReject() {
              tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - end', 'rejected');
              return reject();
            }
          });
          setIsThreeLOPromptVisible(true);
        }
      });
    }
  });
  var _useBridge = useBridge({
      theme: theme,
      surfaceColor: surfaceColor,
      origin: new URL(src).origin,
      api: _objectSpread(_objectSpread({}, createBridgeApi(bridge, {
        userConsentRequired: !!userConsentRequired
      })), {}, {
        onInvoke: function () {
          var _onInvoke = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(payload) {
            var _contextTokenRef$curr, _resp$response;
            var newContextToken, _yield$invoker, data, resp, _resp$errors, _error$message, error, _error$extensions$fie;
            return _regeneratorRuntime.wrap(function _callee3$(_context3) {
              while (1) switch (_context3.prev = _context3.next) {
                case 0:
                  if (!getContextToken) {
                    _context3.next = 6;
                    break;
                  }
                  _context3.next = 3;
                  return getContextToken();
                case 3:
                  _context3.t0 = _context3.sent;
                  _context3.next = 7;
                  break;
                case 6:
                  _context3.t0 = (_contextTokenRef$curr = contextTokenRef.current) === null || _contextTokenRef$curr === void 0 ? void 0 : _contextTokenRef$curr.jwt;
                case 7:
                  newContextToken = _context3.t0;
                  tracing === null || tracing === void 0 || tracing.recordGqlCall('invokeExtension - start');
                  _context3.next = 11;
                  return invoker({
                    call: payload,
                    context: _objectSpread({}, productContext.resolverContext),
                    extensionPayload: extensionPayload,
                    contextToken: newContextToken
                  });
                case 11:
                  _yield$invoker = _context3.sent;
                  data = _yield$invoker.data;
                  resp = data === null || data === void 0 ? void 0 : data.invokeExtension;
                  if (resp !== null && resp !== void 0 && resp.success) {
                    _context3.next = 19;
                    break;
                  }
                  error = resp === null || resp === void 0 || (_resp$errors = resp.errors) === null || _resp$errors === void 0 ? void 0 : _resp$errors[0];
                  if (!((error === null || error === void 0 ? void 0 : error.extensions.errorType) === 'USER_CONSENT_REQUIRED')) {
                    _context3.next = 18;
                    break;
                  }
                  return _context3.abrupt("return", {
                    type: '3lo',
                    authUrl: error === null || error === void 0 || (_error$extensions$fie = error.extensions.fields) === null || _error$extensions$fie === void 0 ? void 0 : _error$extensions$fie.authInfoUrl
                  });
                case 18:
                  return _context3.abrupt("return", {
                    type: 'err',
                    message: (_error$message = error === null || error === void 0 ? void 0 : error.message) !== null && _error$message !== void 0 ? _error$message : 'An error occurred while trying to invoke.'
                  });
                case 19:
                  tracing === null || tracing === void 0 || tracing.recordGqlCall('invokeExtension - end');
                  contextTokenRef.current = resp === null || resp === void 0 ? void 0 : resp.contextToken;
                  return _context3.abrupt("return", {
                    type: 'ok',
                    response: resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.body
                  });
                case 22:
                case "end":
                  return _context3.stop();
              }
            }, _callee3);
          }));
          function onInvoke(_x) {
            return _onInvoke.apply(this, arguments);
          }
          return onInvoke;
        }(),
        onNavigate: navigation.onNavigate,
        onThreeLO: function () {
          var _onThreeLO = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(authUrl, retry) {
            return _regeneratorRuntime.wrap(function _callee4$(_context4) {
              while (1) switch (_context4.prev = _context4.next) {
                case 0:
                  return _context4.abrupt("return", new Promise(function (resolve, reject) {
                    invokeRetriesRef.current.push(retry);
                    tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - start');
                    setThreeLOInfo({
                      authUrl: authUrl,
                      onSuccess: function onSuccess() {
                        tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - end', 'success');
                        resolve();
                        setThreeLOInfo(null);
                        setIsThreeLOPromptVisible(false);
                        return Promise.all(invokeRetriesRef.current.map(function (retry) {
                          return retry();
                        })).then(function () {
                          invokeRetriesRef.current = [];
                        });
                      },
                      onReject: function onReject() {
                        tracing === null || tracing === void 0 || tracing.recordConsentFlow('consent flow - end', 'rejected');
                        return reject();
                      }
                    });
                    setIsThreeLOPromptVisible(true);
                  }));
                case 1:
                case "end":
                  return _context4.stop();
              }
            }, _callee4);
          }));
          function onThreeLO(_x2, _x3) {
            return _onThreeLO.apply(this, arguments);
          }
          return onThreeLO;
        }(),
        getContext: function () {
          var _getContext = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
            return _regeneratorRuntime.wrap(function _callee5$(_context5) {
              while (1) switch (_context5.prev = _context5.next) {
                case 0:
                  return _context5.abrupt("return", _objectSpread({}, productContext.viewContext));
                case 1:
                case "end":
                  return _context5.stop();
              }
            }, _callee5);
          }));
          function getContext() {
            return _getContext.apply(this, arguments);
          }
          return getContext;
        }(),
        fetchProduct: function () {
          var _fetchProduct = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(_ref4) {
            var restPath, product, fetchRequestInit, isMultipartFormData, response, status, statusText, headers, isAttachment, body;
            return _regeneratorRuntime.wrap(function _callee6$(_context6) {
              while (1) switch (_context6.prev = _context6.next) {
                case 0:
                  restPath = _ref4.restPath, product = _ref4.product, fetchRequestInit = _ref4.fetchRequestInit, isMultipartFormData = _ref4.isMultipartFormData;
                  _context6.next = 3;
                  return productFetchClient(restPath, product, fetchRequestInit, isMultipartFormData);
                case 3:
                  response = _context6.sent;
                  status = response.status, statusText = response.statusText;
                  headers = Object.fromEntries(response.headers.entries());
                  isAttachment = isBinaryContentType(response.headers.get('content-type'));
                  if (!response.body) {
                    _context6.next = 13;
                    break;
                  }
                  _context6.next = 10;
                  return response.text();
                case 10:
                  _context6.t0 = _context6.sent;
                  _context6.next = 14;
                  break;
                case 13:
                  _context6.t0 = undefined;
                case 14:
                  body = _context6.t0;
                  return _context6.abrupt("return", {
                    headers: headers,
                    status: status,
                    statusText: statusText,
                    body: body,
                    isAttachment: isAttachment
                  });
                case 16:
                case "end":
                  return _context6.stop();
              }
            }, _callee6);
          }));
          function fetchProduct(_x4) {
            return _fetchProduct.apply(this, arguments);
          }
          return fetchProduct;
        }(),
        getFrameId: function getFrameId() {
          var _extension$properties;
          return (_extension$properties = extension.properties.frameId) !== null && _extension$properties !== void 0 ? _extension$properties : null;
        }
      }),
      customBridgeMethods: customBridgeMethods,
      onLoad: onLoad,
      isResizable: isResizable,
      height: height,
      environment: environment,
      extension: extension,
      iframeRef: iframeRef
    }),
    loading = _useBridge.loading,
    onLoadIframeFromProps = _useBridge.iframeProps.onLoad;
  return jsx(Box
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
  , {
    xcss: xcss({
      position: 'relative',
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      height: isResizable || threeLOInfo ? undefined : height,
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      width: width
    })
  }, navigation.getModalJsx(), (loading || threeLOInfo) && jsx(Suspense, {
    fallback: null
  }, jsx(Box, {
    xcss: xcss({
      width: '100%',
      height: '100%'
    })
  }, loading && !threeLOInfo && !isHidden && getLoadingComponent(loadingComponent), threeLOInfo && ThreeLOPrompt({
    authUrl: threeLOInfo.authUrl,
    onSuccess: threeLOInfo.onSuccess,
    metadata: {
      moduleType: extension.type
    }
  }))), !userConsentRequired && jsx(Fragment, null, jsx("iframe", {
    "data-testid": "hosted-resources-iframe",
    "data-forge-iframe": true
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    ,
    css: css({
      border: 'none',
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      display: loading || threeLOInfo || isHidden ? 'none' : 'initial',
      width: '100%'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
    isResizable ? '' : 'height: 100%;'),
    sandbox: "allow-downloads allow-forms allow-modals allow-pointer-lock allow-same-origin allow-scripts",
    allow: "camera; clipboard-write; display-capture; fullscreen; microphone",
    src: src,
    ref: iframeRef,
    onLoad: onLoadIframeFromProps
  })));
});